// components/contracts/ContractDialogs.js
// Dialog per validazione e generazione contratto appalto - V2 con Allegato A dinamico

import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calculator, ClipboardCheck, CheckCircle, AlertCircle, Loader2,
  FileSignature, Calendar, Plus, Trash2, FileText, Info
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

// Validation Modal
export const ValidationModal = ({
  open,
  onOpenChange,
  validationType,
  validationApproved,
  setValidationApproved,
  validationNotes,
  setValidationNotes,
  onValidate
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {validationType === 'computo' ? (
            <>
              <Calculator className="w-5 h-5 text-[#0078C8]" />
              Valida Computo Metrico
            </>
          ) : (
            <>
              <ClipboardCheck className="w-5 h-5 text-[#FF7819]" />
              Valida Documenti Amministrativi
            </>
          )}
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div>
          <Label>Esito Validazione</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <button
              onClick={() => setValidationApproved(true)}
              className={`p-4 rounded-lg border-2 transition-all ${
                validationApproved === true
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}
              data-testid="approve-btn"
            >
              <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${
                validationApproved === true ? 'text-green-500' : 'text-gray-400'
              }`} />
              <p className="text-sm font-medium text-center">Approvato</p>
            </button>
            
            <button
              onClick={() => setValidationApproved(false)}
              className={`p-4 rounded-lg border-2 transition-all ${
                validationApproved === false
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 hover:border-amber-300'
              }`}
              data-testid="reject-btn"
            >
              <AlertCircle className={`w-8 h-8 mx-auto mb-2 ${
                validationApproved === false ? 'text-amber-500' : 'text-gray-400'
              }`} />
              <p className="text-sm font-medium text-center">Richiedi Integrazioni</p>
            </button>
          </div>
        </div>
        
        <div>
          <Label>
            Note {validationApproved === false ? '(obbligatorio)' : '(opzionale)'}
          </Label>
          <Textarea
            value={validationNotes}
            onChange={(e) => setValidationNotes(e.target.value)}
            placeholder={
              validationApproved === false
                ? 'Specifica cosa è necessario integrare o correggere...'
                : 'Note opzionali...'
            }
            className="mt-1"
            rows={3}
            data-testid="validation-notes"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button
          onClick={onValidate}
          className={validationType === 'computo' ? 'bg-[#0078C8] hover:bg-[#005fa3]' : 'btn-primary'}
          data-testid="confirm-validation-btn"
        >
          Conferma Validazione
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


// Allegato A Summary Box (Read-only)
const AllegatoASummaryBox = ({ allegatoAData, loading, error }) => {
  if (loading) {
    return (
      <div className="p-4 bg-[#FFF5EE] rounded-lg border border-[#FF7819]/30">
        <div className="flex items-center gap-2 text-[#FF7819]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Caricamento dati Allegato A...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!allegatoAData) {
    return null;
  }

  return (
    <div className="p-4 bg-[#FFF5EE] rounded-lg border border-[#FF7819]/30">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-5 h-5 text-[#FF7819]" />
        <h4 className="font-semibold text-[#3C3C3C]">Oggetto dell'Appalto</h4>
      </div>
      
      <div className="bg-white/50 rounded p-3 mb-3">
        <p className="text-xs text-[#737373] italic">
          L'oggetto dell'appalto sarà determinato automaticamente dall'Allegato A - Capitolato 
          contrattualizzato, generato dalle sole voci del preventivo che compongono l'importo del contratto.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-[#737373]">Preventivo sorgente:</span>
          <p className="font-medium">{allegatoAData.estimate_number}</p>
        </div>
        <div>
          <span className="text-[#737373]">Voci contrattualizzate:</span>
          <p className="font-medium">{allegatoAData.contracted_items_count}</p>
        </div>
      </div>

      {/* Categories included */}
      <div className="mt-3">
        <span className="text-xs text-[#737373]">Categorie incluse nell'Allegato A:</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {allegatoAData.categories_detail?.map(cat => (
            <span 
              key={cat.code}
              className="px-2 py-0.5 bg-[#FF7819]/10 text-[#FF7819] text-xs rounded-full"
            >
              {cat.code} - {cat.name} ({cat.items_count} voci)
            </span>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="mt-4 pt-3 border-t border-[#FF7819]/20">
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <span className="text-[#737373] text-xs">Importo netto</span>
            <p className="font-bold text-[#FF7819]">{formatCurrency(allegatoAData.allegato_a_net_total)}</p>
          </div>
          <div>
            <span className="text-[#737373] text-xs">IVA {allegatoAData.vat_rate}%</span>
            <p className="font-medium">{formatCurrency(allegatoAData.vat_amount)}</p>
          </div>
          <div>
            <span className="text-[#737373] text-xs">Totale con IVA</span>
            <p className="font-bold text-[#3C3C3C]">{formatCurrency(allegatoAData.contract_total_with_vat)}</p>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="mt-3 flex items-start gap-2 text-xs text-[#737373]">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          L'Allegato A verrà generato automaticamente e allegato al PDF del contratto.
          Non è necessario selezionare manualmente le sezioni di lavoro.
        </span>
      </div>
    </div>
  );
};


// Appalto Generation Modal - V2 with Dynamic Allegato A
export const AppaltoModal = ({
  open,
  onOpenChange,
  appaltoForm,
  setAppaltoForm,
  calculatedEndDate,
  selectedTier,
  generatingAppalto,
  onGenerate,
  onUpdateInstallment,
  onRemoveInstallment,
  onAddInstallment,
  calculateEndDate,
  contractId,
  api
}) => {
  const [allegatoAData, setAllegatoAData] = useState(null);
  const [allegatoALoading, setAllegatoALoading] = useState(false);
  const [allegatoAError, setAllegatoAError] = useState(null);

  // Fetch Allegato A data when modal opens
  useEffect(() => {
    const fetchAllegatoA = async () => {
      if (!open || !contractId || !api) return;
      
      setAllegatoALoading(true);
      setAllegatoAError(null);
      
      try {
        // Note: api instance already has /api as baseURL, so we don't prefix here
        const response = await api.get(`/contracts/${contractId}/allegato-a-preview`);
        setAllegatoAData(response.data);
      } catch (err) {
        console.error('Error fetching Allegato A:', err);
        setAllegatoAError(err.response?.data?.detail || 'Errore nel caricamento dei dati');
      } finally {
        setAllegatoALoading(false);
      }
    };

    fetchAllegatoA();
  }, [open, contractId, api]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#FF7819]" />
            Genera Contratto di Appalto
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Data e Luogo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data Contratto</Label>
              <Input
                type="date"
                value={appaltoForm.contract_date}
                onChange={(e) => setAppaltoForm(prev => ({ ...prev, contract_date: e.target.value }))}
                className="mt-1"
                data-testid="contract-date"
              />
            </div>
            <div>
              <Label>Luogo di Firma</Label>
              <Input
                value={appaltoForm.place_of_signature}
                onChange={(e) => setAppaltoForm(prev => ({ ...prev, place_of_signature: e.target.value }))}
                className="mt-1"
                data-testid="place-signature"
              />
            </div>
          </div>

          {/* V2: Allegato A Summary Box (replaces work sections checkboxes) */}
          <div>
            <Label className="mb-2 block">Allegato A - Lavorazioni Contrattualizzate</Label>
            <AllegatoASummaryBox 
              allegatoAData={allegatoAData}
              loading={allegatoALoading}
              error={allegatoAError}
            />
          </div>

          {/* Importi */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Importo Netto Contratto</Label>
                <p className="text-xl font-bold text-[#FF7819] mt-1">
                  {formatCurrency(appaltoForm.total_net_amount)}
                </p>
              </div>
              <div>
                <Label>IVA %</Label>
                <Input
                  type="number"
                  min="0"
                  max="22"
                  value={appaltoForm.vat_rate}
                  onChange={(e) => setAppaltoForm(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 10 }))}
                  className="mt-1 w-20"
                  data-testid="vat-rate"
                />
              </div>
              <div>
                <Label>Totale con IVA</Label>
                <p className="text-xl font-bold text-[#3C3C3C] mt-1">
                  {formatCurrency(appaltoForm.total_net_amount * (1 + appaltoForm.vat_rate / 100))}
                </p>
              </div>
            </div>
          </div>

          {/* Tempistiche */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                Data Inizio Lavori <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={appaltoForm.planned_start_date}
                onChange={(e) => {
                  setAppaltoForm(prev => ({ ...prev, planned_start_date: e.target.value }));
                  if (e.target.value && appaltoForm.working_days_duration) {
                    calculateEndDate(e.target.value, appaltoForm.working_days_duration);
                  }
                }}
                className={`mt-1 ${!appaltoForm.planned_start_date ? 'border-red-300' : ''}`}
                data-testid="start-date"
                required
              />
              {!appaltoForm.planned_start_date && (
                <p className="text-xs text-red-500 mt-1">Inserisci la data di inizio lavori</p>
              )}
            </div>
            <div>
              <Label>Durata (gg lavorativi)</Label>
              <Input
                type="number"
                min="1"
                value={appaltoForm.working_days_duration}
                onChange={(e) => {
                  const days = parseInt(e.target.value) || 60;
                  setAppaltoForm(prev => ({ ...prev, working_days_duration: days }));
                  if (appaltoForm.planned_start_date) {
                    calculateEndDate(appaltoForm.planned_start_date, days);
                  }
                }}
                className="mt-1"
                data-testid="working-days"
              />
            </div>
            <div>
              <Label>Data Fine Prevista</Label>
              <div className="flex items-center h-10 px-3 bg-gray-100 rounded-md mt-1 text-[#3C3C3C]">
                <Calendar className="w-4 h-4 mr-2 text-[#737373]" />
                {calculatedEndDate || '-'}
              </div>
            </div>
          </div>

          {/* Piano Pagamenti */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label>Piano Pagamenti</Label>
              {selectedTier && (
                <span className="text-xs text-[#FF7819] bg-[#FFF5EE] px-2 py-1 rounded">
                  Fascia: {selectedTier.name}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {appaltoForm.payment_installments.map((inst, idx) => (
                <div key={inst.id || `installment-${idx}`} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Input
                    value={inst.label}
                    onChange={(e) => onUpdateInstallment(idx, 'label', e.target.value)}
                    className="flex-1"
                    placeholder="Nome rata"
                  />
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={inst.percentage}
                    onChange={(e) => onUpdateInstallment(idx, 'percentage', parseFloat(e.target.value) || 0)}
                    className="w-20"
                  />
                  <span className="text-sm text-[#737373]">%</span>
                  <span className="w-28 text-right font-medium">
                    {formatCurrency(inst.amount)}
                  </span>
                  {appaltoForm.payment_installments.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveInstallment(idx)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddInstallment}
              className="mt-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi Rata
            </Button>
            
            {/* Totale percentuali */}
            <div className="mt-3 p-2 bg-gray-100 rounded flex justify-between">
              <span className="text-sm">Totale percentuali:</span>
              <span className={`font-medium ${
                appaltoForm.payment_installments.reduce((sum, i) => sum + i.percentage, 0) === 100 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {appaltoForm.payment_installments.reduce((sum, i) => sum + i.percentage, 0)}%
              </span>
            </div>
          </div>

          {/* Allegati Aggiuntivi */}
          <div>
            <Label>Allegati Aggiuntivi al Contratto</Label>
            <p className="text-xs text-[#737373] mb-2">L'Allegato A (Capitolato) è sempre incluso automaticamente</p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[
                { key: 'has_economic_schedule', label: 'Cronoprogramma Economico' },
                { key: 'has_timeline', label: 'Cronoprogramma Lavori' },
                { key: 'has_technical_drawings', label: 'Elaborati Grafici' },
              ].map(att => (
                <label key={att.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <Checkbox
                    checked={appaltoForm[att.key]}
                    onCheckedChange={(checked) => setAppaltoForm(prev => ({ ...prev, [att.key]: checked }))}
                  />
                  <span className="text-sm">{att.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tipo Cliente */}
          <div>
            <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <Checkbox
                checked={appaltoForm.customer_is_consumer}
                onCheckedChange={(checked) => setAppaltoForm(prev => ({ ...prev, customer_is_consumer: checked }))}
              />
              <span className="text-sm">Cliente è consumatore (persona fisica)</span>
            </label>
            <p className="text-xs text-[#737373] ml-6">
              Se selezionato, verranno incluse le clausole di tutela consumatore
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={onGenerate}
            disabled={
              generatingAppalto || 
              allegatoALoading ||
              allegatoAError ||
              appaltoForm.payment_installments.reduce((sum, i) => sum + i.percentage, 0) !== 100 ||
              !appaltoForm.planned_start_date
            }
            className="btn-primary"
            data-testid="generate-appalto-confirm-btn"
          >
            {generatingAppalto ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generazione...
              </>
            ) : (
              <>
                <FileSignature className="w-4 h-4 mr-2" />
                Genera Contratto
              </>
            )}
          </Button>
        </DialogFooter>
        {!appaltoForm.planned_start_date && (
          <p className="text-xs text-red-500 text-center mt-2">
            Inserisci la data di inizio lavori prima di generare il contratto
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
